import { useCallback, useEffect, useState, useRef, useMemo } from 'react'
import router from 'next/router'

import { useTranslation } from 'next-i18next'
import { captureException } from '@sentry/nextjs'

import playCanvasAnalytics from '~/analytics/playcanvas/playcanvasAnalyticsEventLogger'
import { PetState } from '~/redux/slices/pet-state'
import { EventStatus, EventType, REWARD_EVENT, REWARD_FEATURE } from '~/gamification/types/gamification.types'
import { getFirebase, execute } from '~/firebase'
import { setStoryCardIdActionCreator } from '~/redux/slices/story-card'
import { updatePetState } from '~/gamification/apis/gamification.api'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { isAuthenticated } from '~/auth/slices/auth.slice'
import useCurrentUser from '~/hooks/useCurrentUser'
import { AppLanguage } from '~/types/locale'
import { HomeBaseStoryCardId } from '~/types/storyCard'
import {
  closeHomebase,
  resetPetState,
  resetScrollToBase,
  selectPetState,
  selectScrollToBase,
  setPetState,
} from '~/redux/slices/homebase'

import {
  selectEvents,
  selectGems,
  selectMathEnergyOrbs,
  setShowHomebaseClosingX,
  setShowShopButton,
  showHomebaseClosingX,
} from '~/gamification/slices/gamification.slice'

import { useGetAvatarMetadataQuery } from '~/redux/apis/user-api'
import appConfig from '~/config/app'
import {
  PlaycanvasEvent,
  AvatarMetadata,
  ActivitiesDone,
  PlaycanvasActions,
  PlaycanvasTypes,
  PlaycanvasRequest,
} from '~/homebase/types/Homebase.types'

import { GemShopInventory } from '~/types/gem-shop'

import { TabClick } from '~/games/types/games.types'

import {
  useEquipItemMutation,
  useLazyGetGemStoreDataQuery,
  useGetUserItemsQuery,
  useUnequipItemMutation,
  gemShopApi,
} from '~/redux/apis/gem-shop-api'

import {
  DragonData,
  useCreateDragonMutation,
  useEvolveDragonMutation,
  useGetDragonQuery,
} from '~/redux/apis/dragon-api'

import { UserItems } from '~/components/Modals/GemShop/GemShop.types'
import { useLazyGetActivitiesSinceDateQuery } from '~/redux/apis/activity-api'
import {
  selectdidPurchase,
  selectShowGemShop,
  setDidPurchase,
  setShowCategory,
  setShowItem,
  setShowSection,
  showGemShop,
} from '~/redux/slices/gem-shop'

import { rewardApi, useSetRewardsMutation, useUpdateEventDataMutation } from '~/redux/apis/reward-api'

import { useGetBaseSrcKey } from '~/hooks/useGetBaseSrcKey'

import useAvatarSrc from '~/shared/hooks/useAvatarSrc'

import ResponsiveImage from '~/components/Image/ResponsiveImage'

import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import TimesIcon from '~/components/Icons/svgs/TimesIcon'

import ROUTES from '~/shared/constants/routes.const'

import ShopAnalytics from '~/analytics/shop/shopAnalytics'
import { ShopEvents, ShopOpenMethods } from '~/analytics/shop/shopEvents.types'
import homebaseAnalytics from '~/analytics/homebase/homebaseAnalytics'
import { HomebaseEvents, PetViewOpenCloseMethods } from '~/analytics/homebase/homebaseEvents.types'

interface petEvent {
  id: HomeBaseStoryCardId
  mathEnergyOrbs?: number
}

const initialPetState: PetState = {
  eggState: 0,
  dragonPresent: false,
  eggChosen: null,
  name: 'Mathagon',
  level: 0,
  dragonRank: 0,
  lifeLength: 0,
  stillHatching: true,
  kudosOrbs: 0,
  totalMathEnergy: 0,
  totalAffection: 0,
  currentMathEnergyLevel: 0,
  currentAffectionLevel: 50,
  lastTimeStamp: 0,
  hatchingCooldown: 0,
  hatchingEnergyCooldown: [0, 0, 0, 0, 0],
  tricksLearned: [],
  currentTrickLearning: null,
  currentTrickProgress: 0,
  trickLearningCooldown: 0,
}

interface PlayCanvasState {
  petState?: PetState
  completedEventIds?: PlaycanvasEvent[]
  mathEnergyOrbs?: number
  avatarMetadata?: AvatarMetadata
  petTrick?: string
  language?: AppLanguage
  pause?: boolean
  inventoryItems?: GemShopInventory
  userItems?: UserItems
  dragon?: DragonData
  gems?: number
  buyStatus?: { data: { success: boolean; errorMsg: string } }
  upgradeStatus?: { data: { success: boolean; errorMsg: string } }
  activitiesDone?: ActivitiesDone[]
  updateInventory?: boolean
  activeTab?: string
  text?: { hint: string; loading: string }
}

function usePetState(iframe: HTMLIFrameElement | undefined): {
  petState: PetState | undefined
  updatePlaycanvas?: (state: PlayCanvasState) => void
} {
  const { t } = useTranslation('homebase')
  const [isReady, setIsReady] = useState(false)
  const [hasInitialized, setHasInitialized] = useState<boolean>(false)
  const [dragonEvolution, setDragonEvolution] = useState<string | undefined>(undefined)
  const [setRewards] = useSetRewardsMutation()

  const updatePlaycanvas = useCallback(
    (message: PlayCanvasState) => {
      const postMessage = iframe?.contentWindow
      if (!postMessage) return
      postMessage.postMessage(message, playcanvasUrl.origin)
    },
    [iframe],
  )
  const [hasFetched, setHasFetched] = useState<boolean>(false)
  // The fed-kudos total the game holds. Firestore only reaches the game when it is ahead of that (another
  // tab fed the dragon); the echo of our own saves never is, and resending it would rewind a fast feed.
  const gameKudosRef = useRef(0)
  const [equipItem] = useEquipItemMutation<Record<string, boolean>>()
  const [unequipItem] = useUnequipItemMutation<Record<string, boolean>>()
  const [createDragon] = useCreateDragonMutation<Record<string, boolean>>()
  const [evloveDragon, evolveStatus] = useEvolveDragonMutation<Record<string, boolean>>()
  const user = useCurrentUser()

  const dragon = useGetDragonQuery(undefined, { skip: !user?.id })
  const [getActivitiesSinceDate] = useLazyGetActivitiesSinceDateQuery()

  const gems = useAppSelector(selectGems)
  const petState = useAppSelector(selectPetState) || initialPetState
  const mathEnergyOrbs = useAppSelector(selectMathEnergyOrbs)
  const didPurchase = useAppSelector(selectdidPurchase)
  const isShopOpen = useAppSelector(selectShowGemShop)
  const events = useAppSelector(selectEvents)
  const hasFetchedEvents = Boolean(events)
  const authenticated = useAppSelector(isAuthenticated)
  const { data: metadata, isSuccess: hasLoadedMetadata } = useGetAvatarMetadataQuery(user, { skip: !user?.avatarUrl })
  const language = router.locale as AppLanguage
  const [getInventoryItems, inventoryItems] = useLazyGetGemStoreDataQuery()
  const userItems = useGetUserItemsQuery(undefined, { skip: !user?.id })
  const dispatch = useAppDispatch()

  const completedEventIds = useMemo(
    () =>
      (events || [])
        .filter((event) => event.type === EventType.PET && event.status === EventStatus.COMPLETED)
        .map((event) => event.id as PlaycanvasEvent),
    [events],
  )
  const getInventory = useCallback((): void => {
    if (!dragonEvolution) return
    getInventoryItems({ language, dragonEvolution }).then((response) => {
      updatePlaycanvas({
        inventoryItems: response.data,
      })
    })
  }, [getInventoryItems, dragonEvolution, language, updatePlaycanvas])

  useEffect(() => {
    const evolutionData = dragon.data?.data?.evolution
    if (evolutionData && dragonEvolution !== evolutionData) setDragonEvolution(evolutionData)
    if (!petState.stillHatching && evolutionData === 'egg') {
      evloveDragon().then(() => {
        dispatch(gemShopApi.util.invalidateTags(['GemShop']))
      })
    }
  }, [dragon, setDragonEvolution, dragonEvolution, petState, dispatch, evloveDragon])

  const getActivitiesDone = useCallback(() => {
    if (petState.lastTimeStamp) {
      getActivitiesSinceDate({ startDate: new Date(petState.lastTimeStamp).toISOString() })
        .unwrap()
        .then((activitiesDone) => {
          updatePlaycanvas({ activitiesDone })
        })
    }
  }, [petState, getActivitiesSinceDate, updatePlaycanvas])

  useEffect(() => {
    if (inventoryItems.isUninitialized && dragon.isSuccess && isReady) {
      getInventory()
    }
  }, [inventoryItems, dragon, isReady, getInventory])

  useEffect(() => {
    if (evolveStatus.isSuccess && dragonEvolution) {
      getInventory()
    }
  }, [evolveStatus, dragonEvolution, getInventory])

  useEffect(() => {
    if (!hasInitialized || !updatePlaycanvas) return
    updatePlaycanvas({
      completedEventIds,
      mathEnergyOrbs,
      avatarMetadata: metadata,
      language,
      gems,
      userItems: userItems.data,
    })
    if (hasLoadedMetadata) {
      updatePlaycanvas({
        avatarMetadata: metadata,
      })
    }
  }, [
    hasInitialized,
    updatePlaycanvas,
    mathEnergyOrbs,
    completedEventIds,
    metadata,
    language,
    hasLoadedMetadata,
    gems,
    userItems,
  ])

  useEffect(() => {
    if (!hasInitialized || !updatePlaycanvas) return
    if ((petState.totalKudosGiven ?? 0) <= gameKudosRef.current) return
    gameKudosRef.current = petState.totalKudosGiven ?? 0
    // The pet state's kudosOrbs is the other tab's copy; send ours, and refetch the kudos that tab just spent
    updatePlaycanvas({ petState: { ...petState, kudosOrbs: mathEnergyOrbs ?? petState.kudosOrbs }, mathEnergyOrbs })
    dispatch(rewardApi.util.invalidateTags(['Rewards']))
  }, [hasInitialized, updatePlaycanvas, petState, mathEnergyOrbs, dispatch])

  useEffect(() => {
    if (!isShopOpen && didPurchase) {
      messagePCHomebase({ updateInventory: true })
      dispatch(setDidPurchase(false))
    }
  }, [didPurchase, isShopOpen, dispatch])

  useEffect(() => {
    if (!updatePlaycanvas || hasInitialized || !hasFetched || !hasFetchedEvents || !isReady || !userItems.isSuccess) {
      return
    }
    updatePlaycanvas({
      petState,
      completedEventIds,
      mathEnergyOrbs,
      language,
      userItems: userItems.data,
      dragon: dragon.data,
      gems,
    })
    gameKudosRef.current = petState.totalKudosGiven ?? 0
    setHasInitialized(true)
  }, [
    createDragon,
    updatePlaycanvas,
    hasInitialized,
    petState,
    mathEnergyOrbs,
    hasFetchedEvents,
    completedEventIds,
    language,
    isReady,
    hasFetched,
    dragon,
    userItems,
    gems,
  ])

  useEffect(() => {
    if (hasFetched || !authenticated) return
    const fetchDocs = async (): Promise<void> => {
      const { getDoc, doc, fireStore } = await getFirebase()
      const petStateDocument = await getDoc(doc(fireStore, 'users', String(user?.id), 'homebase', 'pet-state'))
      const fetchedPetState = petStateDocument.data() as PetState | undefined
      const newPetState = fetchedPetState || initialPetState
      dispatch(setPetState(newPetState))
      setHasFetched(true)
    }
    fetchDocs()
  }, [hasFetched, setHasFetched, user, authenticated, dispatch])

  useEffect(() => {
    if (!hasFetched || !authenticated) return
    const run = async (): Promise<() => void> => {
      const { doc, fireStore, onSnapshot } = await getFirebase()
      const petStateRef = doc(fireStore, 'users', String(user?.id), 'homebase', 'pet-state')

      const unsubscribePetState = onSnapshot(petStateRef, (_doc) => {
        const newPetState = _doc.data() as PetState | undefined
        if (newPetState) {
          dispatch(setPetState(newPetState))
        }
      })
      return () => {
        dispatch(resetPetState(undefined))
        unsubscribePetState()
      }
    }
    const cleanup = run()
    return () => {
      execute(cleanup)
    }
  }, [hasFetched, user, authenticated, dispatch])

  const intialHomebase = useCallback(
    (request: string) => {
      if (iframe && iframe.contentWindow) {
        if (request === 'avatarUrl') {
          setIsReady(true)
        } else if (request === 'petState') {
          setIsReady(true)
        } else if (request === 'activitiesDone') {
          getActivitiesDone()
        }
      }
    },
    [iframe, getActivitiesDone],
  )

  const [updateEventsData] = useUpdateEventDataMutation()

  const handleEvent = useCallback(
    (event: petEvent): void => {
      if (Object.values(HomeBaseStoryCardId).includes(event.id)) {
        dispatch(setStoryCardIdActionCreator(event.id))
      }
      updateEventsData([
        {
          type: EventType.PET,
          status: EventStatus.PENDING,
          ...event,
          eventFeature: REWARD_FEATURE.PET,
          eventType: REWARD_EVENT.FEED_DRAGON,
        },
      ])
    },
    [dispatch, updateEventsData],
  )

  const getHintText = useCallback(() => {
    const randomHint = Math.floor(Math.random() * 5)
    switch (randomHint) {
      case 0:
        return t('loadscreenHint.feedDragon')
      case 1:
        return t('loadscreenHint.earnKudos')
      case 2:
        return t('loadscreenHint.earnGems')
      case 3:
        return t('loadscreenHint.buyStuff')
      case 4:
        return t('loadscreenHint.wardrobe')
      case 5:
        return t('loadscreenHint.moreMath')
      default:
        return t('loadscreenHint.feedDragon')
    }
  }, [t])

  useEffect(() => {
    window.onmessage = function (e) {
      if (e.origin === playcanvasUrl.origin) {
        playCanvasAnalytics.logPlayCanvasEvent(e.data)
        // check that the message is coming from playcanvas
        const data = e.data
        if (data.type === PlaycanvasTypes.EVENT && data.event) {
          handleEvent(data.event)
        } else if (data.type === PlaycanvasTypes.REQUEST) {
          if (
            data.request === PlaycanvasRequest.PET_STATE ||
            data.request === PlaycanvasRequest.AVATAR_URL ||
            data.request === PlaycanvasRequest.ACTIVITIES_DONE
          ) {
            intialHomebase(data.request)
          }
          if (data.request === PlaycanvasRequest.LOAD_TEXT) {
            updatePlaycanvas({
              text: { hint: getHintText(), loading: t('loading') },
            })
          }
          if (data.request === PlaycanvasRequest.OPEN_SHOP) {
            dispatch(showGemShop())
            if (data.sku) {
              dispatch(setShowItem(data.sku))
            }
            if (data.section) {
              dispatch(setShowSection(data.section))
            }
            if (data.category) {
              dispatch(setShowCategory(data.category))
            }
          }
        } else if (data.type === PlaycanvasTypes.STATE) {
          // PetCam shares the origin and also posts state; it saves through its own listener
          if (e.source !== iframe?.contentWindow) return
          gameKudosRef.current = Math.max(gameKudosRef.current, data.petState?.totalKudosGiven ?? 0)
          updatePetState(data.petState).catch(captureException)
        } else if (data.type === PlaycanvasTypes.ACTION) {
          if (data.action === PlaycanvasActions.OPEN_PAGE) {
            if (data.to === ROUTES.DASHBOARD) {
              if (router.pathname === ROUTES.DASHBOARD) {
                dispatch(closeHomebase())
              } else {
                router.push(ROUTES.DASHBOARD)
              }
            } else if (data.to === ROUTES.BOOTCAMP) {
              router.push(ROUTES.BOOTCAMP)
            }
          }

          if (data.action === PlaycanvasActions.HIDE_X) {
            dispatch(setShowHomebaseClosingX(false))
          } else if (data.action === PlaycanvasActions.SHOW_X) {
            dispatch(setShowHomebaseClosingX(true))
          }

          if (data.action === PlaycanvasActions.HIDE_SHOP_BUTTON) {
            dispatch(setShowShopButton(false))
          } else if (data.action === PlaycanvasActions.SHOW_SHOP_BUTTON) {
            dispatch(setShowShopButton(true))
          }

          if (data.action === PlaycanvasActions.MAKE_AVATAR) {
            dispatch(closeHomebase())
            router.replace('/avatar')
          }
          if (data.action === PlaycanvasActions.COLLECT_GEMS && data.gems) {
            setRewards({
              xp: 0,
              kudos: 0,
              gems: data.gems,
              feature: REWARD_FEATURE.PET,
              event_id: `${REWARD_FEATURE.PET}#${REWARD_EVENT.COLLECT_GEMS}`,
            })
          }
          if (data.action === PlaycanvasActions.GIVE_KUDOS && data.kudos) {
            setRewards({
              xp: 0,
              kudos: data.kudos,
              gems: 0,
              feature: REWARD_FEATURE.PET,
              event_id: `${REWARD_FEATURE.PET}#${REWARD_EVENT.RECEIVE_KUDOS}`,
            })
          }
        } else if (data.type === PlaycanvasTypes.DRAGON) {
          if (data.action === PlaycanvasActions.CREATE) {
            createDragon()
          }
          if (data.action === PlaycanvasActions.EVOLVE) {
            evloveDragon().then(() => {
              dispatch(gemShopApi.util.invalidateTags(['GemShop']))
            })
          }
        } else if (data.type === PlaycanvasTypes.ITEMS && data.sku) {
          if (data.action === PlaycanvasActions.EQUIP) {
            equipItem(data.sku)
          }
          if (data.action === PlaycanvasActions.UNEQUIP) {
            unequipItem(data.sku)
          }
        }
      }
    }
  }, [
    dispatch,
    handleEvent,
    intialHomebase,
    createDragon,
    equipItem,
    unequipItem,
    evloveDragon,
    user,
    setRewards,
    updatePlaycanvas,
    t,
    getHintText,
    iframe,
  ])

  return {
    petState,
    updatePlaycanvas: isReady && hasInitialized ? updatePlaycanvas : undefined,
  }
}

export const playcanvasUrl = appConfig.playcanvas.homebase

export function messagePCHomebase(message: PlayCanvasState | { type: PlaycanvasTypes; sku: string }): void {
  const iframe = document.getElementsByClassName('iframe')[0] as HTMLIFrameElement
  const postMessage = iframe?.contentWindow
  if (!postMessage) return
  postMessage.postMessage(message, playcanvasUrl.origin)
}
const mediaQuery25 = [{ screen: '(min-width: 280px)', width: '25px' }]
const mediaQuery35 = [{ screen: '(min-width: 280px)', width: '35px' }]
const mediaQuery100 = [{ screen: '(min-width: 280px)', width: '100px' }]

export function Homebase(): React.ReactElement {
  const { t } = useTranslation(['homebase', 'generics'])
  const [iframe, setIframe] = useState<HTMLIFrameElement | undefined>(undefined)
  const dispatch = useAppDispatch()
  const showClosingX = useAppSelector(showHomebaseClosingX)
  const baseSrcKey = useGetBaseSrcKey()

  const updatePlaycanvas = useCallback(
    (message: PlayCanvasState) => {
      const postMessage = iframe?.contentWindow
      if (!postMessage) return
      postMessage.postMessage(message, playcanvasUrl.origin)
    },
    [iframe],
  )

  useEffect(() => {
    setIframe(document.getElementsByClassName('iframe')[0] as HTMLIFrameElement)
  }, [])

  usePetState(iframe)

  const homebaseRef = useRef<HTMLInputElement>(null)
  const scrollToBase = useAppSelector(selectScrollToBase)

  useEffect(() => {
    if (scrollToBase) {
      if (!homebaseRef.current) return
      homebaseRef.current.scrollIntoView({ behavior: 'smooth' })
      dispatch(resetScrollToBase())
    }
  }, [homebaseRef, scrollToBase, dispatch])

  const onCloseBaseClick = (): void => {
    dispatch(closeHomebase())
  }

  const [isActiveTab, setIsActiveTab] = useState(TabClick.HOME)

  const toggleRef = useRef<HTMLInputElement>(null)
  const tabClassNames =
    'w-full text-center item-center rounded-t-3xl pt-1 -mb-5 pb-5 border-4 border-purple-dark-top hover:cursor-pointer hover:-mt-2 '
  const activeTab = 'bg-purple-dark-top text-center text-white'
  const inactiveTab = 'bg-white text-black'

  const [showTabs, setShowTabs] = useState(false)

  const messageHandler = useCallback((event: any): void => {
    if (event.origin === playcanvasUrl.origin) {
      const messageData = event.data
      if (messageData.action) {
        if (messageData.action === PlaycanvasActions.HIDE_HOMEBASE_TABS) {
          setShowTabs(false)
        } else if (messageData.action === PlaycanvasActions.SHOW_HOMEBASE_TABS) {
          setShowTabs(true)
        } else if (messageData.action === PlaycanvasActions.SELECTED_HOMEBASE_TABS) {
          setIsActiveTab(messageData.activeTab)
        }
      }
    }
  }, [])

  useEffect(() => {
    window.addEventListener('message', messageHandler)
    return () => window.removeEventListener('message', messageHandler)
  }, [messageHandler])

  const avatarImgSrcKey = useAvatarSrc()

  return (
    <div>
      {showTabs && (
        <div className="flex lg:w-1/2 pt-2 h-14" ref={toggleRef}>
          <div
            className={`${
              isActiveTab === TabClick.HOME ? `${activeTab}` : inactiveTab
            } ${tabClassNames} overflow-hidden`}
            onClick={() => {
              if (isActiveTab !== TabClick.HOME) {
                homebaseAnalytics.logEvent({
                  event: HomebaseEvents.CLOSE_PET_VIEW,
                  method: PetViewOpenCloseMethods.TAB,
                })
              }
              setIsActiveTab(TabClick.HOME)
              updatePlaycanvas({
                activeTab: TabClick.HOME,
              })
            }}
          >
            <div className="flex flex-col items-center transform duration-500 md:hover:cursor-pointer md:hover:scale-110">
              <div className="flex">
                <div className="w-4/12 ">
                  <ResponsiveImage
                    height={28}
                    imageClassName="absolute mt-1 -ml-6"
                    imageWidths="25"
                    mediaQuery={mediaQuery25}
                    srcKey="gamification/badge-bg.png"
                    width={25}
                  />
                  <div className="rounded-full absolute mt-1.5 -ml-5.5 h-5 w-5 overflow-hidden">
                    <ResponsiveImage
                      height={100}
                      imageClassName="min-w-10 -ml-2.5 -mt-1 h-10"
                      imageWidths="100"
                      mediaQuery={mediaQuery100}
                      srcKey={avatarImgSrcKey}
                      width={100}
                    />
                  </div>
                </div>
                <div className="w-full">
                  <p className="mt-1 mx-1 w-full text-left">{t('homebase:mainView')}</p>
                </div>
              </div>
            </div>
          </div>
          <div
            className={tabClassNames + (isActiveTab === TabClick.DRAGON ? activeTab : inactiveTab)}
            onClick={() => {
              if (isActiveTab !== TabClick.DRAGON) {
                homebaseAnalytics.logEvent({ event: HomebaseEvents.OPEN_PET_VIEW, method: PetViewOpenCloseMethods.TAB })
              }
              setIsActiveTab(TabClick.DRAGON)
              updatePlaycanvas({
                activeTab: TabClick.DRAGON,
              })
            }}
          >
            <div className="relative">
              <div className="flex flex-col items-center transform duration-500 md:hover:cursor-pointer md:hover:scale-110 ">
                <div className="flex">
                  <div className="w-4/12">
                    <ResponsiveImage
                      alt={t('navbarIcon')}
                      height={50}
                      imageWidths="35"
                      mediaQuery={mediaQuery35}
                      srcKey={baseSrcKey}
                      width={35}
                    />
                  </div>
                  <div className="w-full">
                    <p className="mt-1 mx-1 w-full text-left">{t('homebase:dragonView')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className={tabClassNames + (isActiveTab === TabClick.SHOP ? activeTab : inactiveTab)}
            onClick={() => {
              ShopAnalytics.logEvent({ event: ShopEvents.OPEN, method: ShopOpenMethods.TAB })
              dispatch(showGemShop())
              updatePlaycanvas({
                activeTab: TabClick.SHOP,
              })
            }}
          >
            <div className="relative">
              <div className="flex flex-col items-center transform duration-500 md:hover:cursor-pointer md:hover:scale-110 ">
                <div className="flex items-center">
                  <div className="w-4/12">
                    <ResponsiveImage
                      height={65}
                      imageClassName="mt-1"
                      imageWidths="42"
                      mediaQuery={mediaQuery35}
                      srcKey="gamification/homeBase/Frame 482822.png"
                      width={53}
                    />
                  </div>
                  <div className="w-full">
                    <p className="mb-0.5 mx-1 w-full text-left">{t('homebase:storeView')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="w-full h-full overflow-y-auto bg-purple-gdark rounded" data-test-id="homebase" ref={homebaseRef}>
        {showClosingX && (
          <div className="relative">
            <button
              aria-label={t('generics:ariaLabelButtons.closeWindow')}
              className="absolute right-4 top-4"
              onClick={onCloseBaseClick}
            >
              <SVGIcon icon={TimesIcon} pathClassName="text-white" size={24} />
            </button>
          </div>
        )}
        <div>
          <iframe className="iframe w-full h-60vh lg:h-144 rounded" src={playcanvasUrl.href} />
        </div>
      </div>
    </div>
  )
}
